import { useEffect } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SUPPORTED_LANGUAGES = ['sv', 'en', 'da', 'no', 'fi'];

// Get default language based on domain - matches LanguageRedirect logic
const getDefaultLanguageForDomain = (): string => {
  if (typeof window === 'undefined') {
    return 'en'; // Default for SSR/prerendering
  }
  const hostname = window.location.hostname;
  // .se domain defaults to Swedish, .com and others default to English
  if (hostname.endsWith('.se') || hostname === 'estylingstudio.se') {
    return 'sv';
  }
  return 'en';
};

export const LanguageLayout = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      // Update HTML lang attribute to match current language
      document.documentElement.lang = lang;
    } else if (lang && !SUPPORTED_LANGUAGES.includes(lang)) {
      // Invalid language, redirect to domain-appropriate default
      const defaultLang = getDefaultLanguageForDomain();
      const newPath = location.pathname.replace(`/${lang}`, `/${defaultLang}`);
      navigate(newPath + location.search, { replace: true });
    }
  }, [lang, i18n, navigate, location]);

  return <Outlet />;
};

// Helper hook to get current language from URL
export const useCurrentLanguage = () => {
  const { lang } = useParams<{ lang: string }>();
  const defaultLang = getDefaultLanguageForDomain();
  return lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : defaultLang;
};

// Helper to build language-aware paths
export const buildLocalizedPath = (path: string, lang: string) => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `/${lang}/${cleanPath}`.replace(/\/+$/, '/').replace(/\/\//g, '/');
};

export { SUPPORTED_LANGUAGES, getDefaultLanguageForDomain };
